import { useEffect, useRef } from 'react';
import * as React from 'react';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import GiphyImg from '@sdk/assets/images/giphy_icon.png';
import SearchImg from '@sdk/assets/svg/Icons/32/search-gray.svg';
import useOutsideAlerter from '@sdk/utils/hooks/outsideAlerter';
import { ModalState } from '@sdk/components/ui/modal/useModalState';
import TextInputBase from '@sdk/components/forms/TextInputBase';
import Spinner from '@sdk/components/ui/Spinner';
import Text12 from '@sdk/components/ui/typography/Text12';
import DisplayText from '@sdk/components/ui/DisplayText';
import CloseIcon from '@sdk/components/ui/CloseIcon';
import { GifData } from '@sdk/components/richText/types';

import { useRecentGifs, useGifSearch } from './gifPicker.hooks';
import {
  Container,
  RelativeContainer,
  SearchIcon,
  GifsContainer,
  GifImage,
  GifImageOverlay,
  GifImageContainer,
  GiphyLogo,
  CenteredColumn,
  ClearIcon,
} from './gifPicker.styles';

type Props = {
  pickerState: ModalState;
  selectGif: (gif: GifData) => void;
  sendGif: (gif: GifData) => void;
  onClose: () => void;
};

const GifPicker: React.FC<React.PropsWithChildren<Props>> = ({
  pickerState,
  selectGif,
  sendGif,
  onClose,
}) => {
  const inputRef = useRef<HTMLInputElement>();
  const containerRef = useRef();
  const prevVisibleRef = useRef(pickerState.isVisible);
  const { recentGifs, addGifToRecent } = useRecentGifs();
  const { isLoading, debouncedSearch, searchResults, searchQuery, setSearchQuery } = useGifSearch();

  const onOutsideClick = (): void => {
    pickerState.close();
    setTimeout(() => onClose?.());
  };
  useOutsideAlerter(containerRef, onOutsideClick);

  useEffect(() => {
    if (!prevVisibleRef.current && pickerState.isVisible) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: Silence mobile error due to lack of DOM lib
      inputRef.current?.focus?.();
    }

    if (prevVisibleRef.current && !pickerState.isVisible) {
      setSearchQuery('');
    }

    prevVisibleRef.current = pickerState.isVisible;
  }, [pickerState, setSearchQuery]);

  useEffect(() => {
    let gifToSelect = recentGifs[0];
    if (debouncedSearch) {
      gifToSelect = searchResults[0];
    }

    selectGif(gifToSelect);
  }, [debouncedSearch, recentGifs, searchResults, selectGif]);

  const onGifClick = (gif: GifData): void => {
    addGifToRecent(gif);
    sendGif(gif);
  };

  const renderGifImage = (gif: GifData): JSX.Element => (
    <GifImageContainer key={gif.originalUrl} onClick={(): void => onGifClick(gif)}>
      <GifImage src={gif.previewUrl} key={gif.previewUrl} />
      <GifImageOverlay>
        <DisplayText themeColor={ThemeColorType.white} uppercase bold>
          {gettext('send')}
        </DisplayText>
      </GifImageOverlay>
    </GifImageContainer>
  );

  if (!pickerState.isVisible) {
    return null;
  }

  return (
    <Container mTop={10} mLeft={10} mRight={10} ref={containerRef}>
      <RelativeContainer>
        <RelativeContainer>
          <SearchIcon>
            <SearchImg />
          </SearchIcon>
          {debouncedSearch && (
            <ClearIcon>
              <CloseIcon spacing={13} onClick={(): void => setSearchQuery('')} />
            </ClearIcon>
          )}

          <TextInputBase
            ref={inputRef}
            placeholder={gettext('Search')}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore: Silence mobile error due to lack of DOM lib
            onChange={(e): void => setSearchQuery(e.target.value)}
            value={searchQuery}
            paddingLeft={32}
            paddingRight={32}
          />
        </RelativeContainer>

        <GiphyLogo src={GiphyImg} />
      </RelativeContainer>

      <GifsContainer>
        {isLoading && (
          <CenteredColumn>
            <Spinner />
            <Text12 uppercase mTop={10} themeColor={ThemeColorType.iconGray}>
              {gettext('loading gifs...')}
            </Text12>
          </CenteredColumn>
        )}
        {!isLoading && (
          <>
            {!!searchResults.length && !!debouncedSearch && searchResults.map(renderGifImage)}
            {!searchResults.length && !!debouncedSearch && (
              <CenteredColumn>
                <Text12 uppercase mTop={10} themeColor={ThemeColorType.iconGray}>
                  {gettext('no results found for this phrase')}
                </Text12>
              </CenteredColumn>
            )}

            {!!recentGifs.length && !debouncedSearch && recentGifs.map(renderGifImage)}
            {!recentGifs.length && !debouncedSearch && (
              <CenteredColumn>
                <Text12 uppercase mTop={10} themeColor={ThemeColorType.iconGray}>
                  {gettext('no recent gifs. type to search')}
                </Text12>
              </CenteredColumn>
            )}
          </>
        )}
      </GifsContainer>
    </Container>
  );
};

export default GifPicker;
